import { useNavigate, useParams, Link } from 'react-router-dom';
import { ChevronLeft, Package, PackageX } from 'lucide-react';
import { useShopifyOrder } from '../hooks/useShopifyOrders.js';
import { formatCurrency, formatDate } from '../lib/format.js';

const FINANCIAL_BADGE = {
  paid:               { label: 'Payée',         className: 'bg-green-100 text-green-700' },
  pending:            { label: 'En attente',    className: 'bg-yellow-100 text-yellow-700' },
  refunded:           { label: 'Remboursée',    className: 'bg-slate-100 text-slate-600' },
  partially_refunded: { label: 'Part. remb.',   className: 'bg-orange-100 text-orange-700' },
  voided:             { label: 'Annulée',       className: 'bg-red-100 text-red-700' },
};

const FULFILLMENT_BADGE = {
  fulfilled:         { label: 'Expédiée',       className: 'bg-green-100 text-green-700' },
  partial:           { label: 'Partielle',      className: 'bg-yellow-100 text-yellow-700' },
  unfulfilled:       { label: 'Non expédiée',   className: 'bg-slate-100 text-slate-500' },
  restocked:         { label: 'Restockée',      className: 'bg-slate-100 text-slate-500' },
};

function Badge({ map, value }) {
  const conf = map[value] ?? { label: value ?? '—', className: 'bg-slate-100 text-slate-500' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${conf.className}`}>
      {conf.label}
    </span>
  );
}

function AddressBlock({ title, addr }) {
  if (!addr) return null;
  const name = [addr.first_name, addr.last_name].filter(Boolean).join(' ');
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{title}</p>
      {name && <p className="text-sm font-medium text-slate-900">{name}</p>}
      {addr.address1 && <p className="text-sm text-slate-600">{addr.address1}</p>}
      {addr.address2 && <p className="text-sm text-slate-600">{addr.address2}</p>}
      <p className="text-sm text-slate-600">
        {[addr.zip, addr.city].filter(Boolean).join(' ')}
        {addr.country && `, ${addr.country}`}
      </p>
    </div>
  );
}

function addressKey(addr) {
  if (!addr) return '';
  return [addr.first_name, addr.last_name, addr.address1, addr.zip, addr.city, addr.country].join('|');
}

export default function ShopifyOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useShopifyOrder(id);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-32 bg-slate-100 animate-pulse rounded" />
        <div className="h-32 bg-slate-100 animate-pulse rounded-xl" />
        <div className="h-48 bg-slate-100 animate-pulse rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => navigate('/ventes/commandes')}
          className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        >
          <ChevronLeft size={16} aria-hidden /> Retour
        </button>
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error.status === 404 ? 'Commande introuvable.' : `Erreur : ${error.message}`}
        </div>
      </div>
    );
  }

  const order = data?.order;
  const lineItems = data?.line_items ?? [];

  if (!order) return null;

  const raw = order.raw ?? {};
  const shippingAddr = raw.shipping_address ?? null;
  const billingAddr = raw.billing_address ?? null;
  const showBilling = billingAddr && addressKey(billingAddr) !== addressKey(shippingAddr);

  const subtotalCents = order.subtotal_price_cents ?? 0;
  const totalTaxCents = order.total_tax_cents ?? 0;
  const totalDiscountCents = order.total_discounts_cents ?? 0;
  const totalCents = order.total_price_cents ?? 0;

  return (
    <div className="space-y-4">
      {/* Bouton retour */}
      <button
        type="button"
        onClick={() => navigate('/ventes/commandes')}
        className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 transition-colors"
      >
        <ChevronLeft size={16} aria-hidden /> Retour aux commandes
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{order.name ?? `#${order.order_number}`}</h2>
            <p className="text-sm text-slate-500 mt-0.5">{formatDate(order.created_at_shopify)}</p>
          </div>
          <p className="text-2xl font-bold tabular-nums text-slate-900">
            {formatCurrency(totalCents)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge map={FINANCIAL_BADGE} value={order.financial_status} />
          {order.fulfillment_status && (
            <Badge map={FULFILLMENT_BADGE} value={order.fulfillment_status} />
          )}
        </div>

        {/* Client */}
        {order.customer_email && (
          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Client</p>
            <a
              href={`mailto:${order.customer_email}`}
              className="text-sm text-blue-600 hover:underline"
            >
              {order.customer_email}
            </a>
          </div>
        )}
      </div>

      {/* Adresses */}
      {(shippingAddr || billingAddr) && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
          <div className={`grid gap-4 ${showBilling ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
            <AddressBlock title="Livraison" addr={shippingAddr} />
            {showBilling && <AddressBlock title="Facturation" addr={billingAddr} />}
          </div>
        </div>
      )}

      {/* Line items */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Produits commandés</h3>

        {lineItems.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <PackageX size={28} className="text-slate-300" />
            <p className="text-sm text-slate-500">Détail produits non disponible.</p>
            <p className="text-xs text-slate-400">
              Relancer un backfill pour récupérer le détail.
            </p>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="space-y-2 md:hidden">
              {lineItems.map((item) => {
                const lineCents = (item.unit_price_cents ?? 0) * item.quantity - (item.total_discount_cents ?? 0);
                return (
                  <div key={item.id} className="border border-slate-100 rounded-lg px-3 py-2.5 space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 leading-snug">
                          {item.title ?? '—'}
                          {item.variant_title && (
                            <span className="text-slate-500"> · {item.variant_title}</span>
                          )}
                        </p>
                        {item.sku && (
                          <div className="flex items-center gap-1.5 mt-0.5">
                            {item.product ? (
                              <Link
                                to={`/ventes/produits/${encodeURIComponent(item.sku)}`}
                                className="text-xs font-mono text-blue-600 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {item.sku}
                              </Link>
                            ) : (
                              <span className="text-xs font-mono text-slate-400">{item.sku}</span>
                            )}
                            {item.product ? (
                              <Package size={11} className="text-green-500" aria-label="En inventaire" />
                            ) : (
                              <PackageX size={11} className="text-slate-300" aria-label="Absent de l'inventaire" />
                            )}
                          </div>
                        )}
                        {item.product?.identification_id && (
                          <p className="text-[11px] font-mono text-slate-400 mt-0.5">ID {item.product.identification_id}</p>
                        )}
                      </div>
                      <p className="text-sm font-semibold tabular-nums text-slate-900 shrink-0">
                        {formatCurrency(lineCents)}
                      </p>
                    </div>
                    <p className="text-xs text-slate-500">
                      {item.quantity} × {formatCurrency(item.unit_price_cents ?? 0)}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left pb-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">SKU</th>
                    <th className="text-left pb-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Produit</th>
                    <th className="text-right pb-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Qté</th>
                    <th className="text-right pb-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Prix u.</th>
                    <th className="text-right pb-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Sous-total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {lineItems.map((item) => {
                    const lineCents = (item.unit_price_cents ?? 0) * item.quantity - (item.total_discount_cents ?? 0);
                    return (
                      <tr key={item.id}>
                        <td className="py-2.5 pr-4">
                          {item.sku ? (
                            <div className="space-y-0.5">
                              <div className="flex items-center gap-1.5">
                                {item.product ? (
                                  <Link
                                    to={`/ventes/produits/${encodeURIComponent(item.sku)}`}
                                    className="text-xs font-mono text-blue-600 hover:underline"
                                  >
                                    {item.sku}
                                  </Link>
                                ) : (
                                  <span className="text-xs font-mono text-slate-400">{item.sku}</span>
                                )}
                                {item.product ? (
                                  <Package size={11} className="text-green-500 shrink-0" aria-label="En inventaire" />
                                ) : (
                                  <PackageX size={11} className="text-slate-300 shrink-0" aria-label="Absent de l'inventaire" />
                                )}
                              </div>
                              {item.product?.identification_id && (
                                <p className="text-[11px] font-mono text-slate-400">ID {item.product.identification_id}</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="py-2.5 pr-4 text-slate-800">
                          {item.title ?? '—'}
                          {item.variant_title && (
                            <span className="text-slate-400 ml-1">· {item.variant_title}</span>
                          )}
                        </td>
                        <td className="py-2.5 pr-4 text-right tabular-nums text-slate-700">{item.quantity}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums text-slate-700">
                          {formatCurrency(item.unit_price_cents ?? 0)}
                        </td>
                        <td className="py-2.5 text-right tabular-nums font-semibold text-slate-900">
                          {formatCurrency(lineCents)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Bloc totaux */}
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-1.5 max-w-xs ml-auto">
          <div className="flex justify-between text-sm text-slate-600">
            <span>Sous-total</span>
            <span className="tabular-nums">{formatCurrency(subtotalCents)}</span>
          </div>
          {totalDiscountCents > 0 && (
            <div className="flex justify-between text-sm text-green-600">
              <span>Réductions</span>
              <span className="tabular-nums">−{formatCurrency(totalDiscountCents)}</span>
            </div>
          )}
          {totalTaxCents > 0 && (
            <div className="flex justify-between text-sm text-slate-600">
              <span>Taxes</span>
              <span className="tabular-nums">{formatCurrency(totalTaxCents)}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-bold text-slate-900 pt-1 border-t border-slate-100">
            <span>Total TTC</span>
            <span className="tabular-nums">{formatCurrency(totalCents)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
